import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { manualPayment, orderDetails } from "../../../../services/orderService";
import { Loader2, CreditCard, Users, Calendar, Split, Star, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmationPopup from "../../../../components/common/ConfirmationPopup";

const OrderDetails = ({ orderId }) => {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["orderDetails", orderId],
    queryFn: () => orderDetails({ id: orderId }),
    enabled: !!orderId,
  });

  const { mutate: manualPaymentHandler, isPending: manualPayLoading } = useMutation({
    mutationFn: async () => {
      const res = await manualPayment({ orderId: order?.orderId });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Manual payment recorded successfully!");
        queryClient.invalidateQueries(["orderDetails", orderId]);
        queryClient.invalidateQueries(["order"]);
      } else {
        toast.warning(res?.message || "Something went wrong!");
      }
    },
    onError: (err) => {
      toast.error("Failed to record manual payment!");
      console.error(err);
    },
  });

  const handleConfirmAction = (action) => {
    if (action.button_type === "confirm") {
      manualPaymentHandler();
    }
    setConfirmOpen(false);
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-60">
        <Loader2 className="animate-spin text-gray-500" size={28} />
      </div>
    );

  if (isError || !data?.data) return <div className="text-center text-red-500 py-10">Failed to load order details.</div>;

  const order = data.data;

  const feedbackMap = {};
  order.transactionDetails?.forEach((tx) => {
    feedbackMap[tx._id] = { rating: tx.rating, comment: tx.comment };
  });

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <img src={order.organizationData?.logo?.url || "/placeholder.png"} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
          <div>
            <h2 className="text-lg font-semibold capitalize">{order.organizationData?.name}</h2>
            <p className="text-sm text-gray-500">Order ID: {order.orderId}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {order.status === "pending" || order.status === "partial" ? (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={loading}
              className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90 transition text-sm"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {loading ? "Processing..." : `Manual Payment ($${order?.remainingAmount.toFixed(2)})`}
            </button>
          ) : (
            <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-md font-medium">Payment Completed</span>
          )}

          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              order.status === "paid"
                ? "bg-green-100 text-green-700"
                : order.status === "partial"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {order.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Table</p>
          <p className="font-medium">
            {order.tableData?.tableName} (#{order.tableData?.tableNumber})
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Persons</p>
          <p className="font-medium flex items-center gap-1">
            <Users size={14} /> {order.numberOfPerson}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Created</p>
          <p className="font-medium flex items-center gap-1">
            <Calendar size={14} /> {new Date(order.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-800 mb-2">Ordered Items</h3>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left">Product</th>
                <th className="px-4 py-2 text-left">Variant</th>
                <th className="px-4 py-2 text-center">Qty</th>
                <th className="px-4 py-2 text-right">Price</th>
                <th className="px-4 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-2">{item.productName}</td>
                  <td className="px-4 py-2">{item.variantName}</td>
                  <td className="px-4 py-2 text-center">{item.qty}</td>
                  <td className="px-4 py-2 text-right">${item.price}</td>
                  <td className="px-4 py-2 text-right">${item.subtotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <CreditCard size={18} /> Payment Summary
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <p className="text-gray-600">Subtotal:</p>
          <p className="text-right font-medium">${order.subtotal}</p>
          <p className="text-gray-600">Bill Amount:</p>
          <p className="text-right font-medium">${order.billAmount}</p>
          <p className="text-gray-600">Total Paid:</p>
          <p className="text-right font-semibold text-green-700">${order.payableAmount.toFixed(2)}</p>
        </div>
        {order.status === "partial" && (
          <div className="flex items-center text-yellow-700 text-sm mt-2">
            <AlertCircle size={16} className="mr-1" />
            <span>Partial payment pending</span>
          </div>
        )}
      </div>

      {order.payment?.splits?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <Split size={18} /> Split Payments (
            {order.payment.splitType
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ")}
            )
          </h3>
          {order.payment.splits.map((split, index) => {
            const feedback = feedbackMap[split.transactionId] || {};
            return (
              <div key={index} className="border rounded-lg p-3 mb-3 bg-gray-50 space-y-2">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Split #{index + 1}</span>
                  <span className="font-semibold">${split.amount.toFixed(2)}</span>
                </div>
                {/* <p className="text-xs text-gray-500">
                  Transaction ID:{" "}
                  <span className="font-mono">{split.transactionId}</span>
                </p> */}

                {/* Feedback Section inside Split */}

                {order.payment?.splitType === "split_item" && (
                  <table className="w-full text-xs border-t mt-2">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="py-1 text-left">Item</th>
                        <th className="py-1 text-center">Qty</th>
                        <th className="py-1 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {split.items.map((sItem, idx2) => (
                        <tr key={idx2} className="border-t">
                          <td className="py-1">{sItem.productName}</td>
                          <td className="py-1 text-center">{sItem.qty}</td>
                          <td className="py-1 text-right">${sItem.subtotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {feedback.rating > 0 || feedback.comment ? (
                  <div className="bg-white rounded-md p-2 border mt-2">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} className={`${i < feedback.rating ? "text-yellow-500 fill-yellow-400" : "text-gray-300"}`} />
                      ))}
                    </div>
                    {feedback.comment && <p className="text-xs text-gray-600 italic">“{feedback.comment}”</p>}
                  </div>
                ) : (
                  ""
                )}
                {order.payment?.splitType === "split_amount" && split.noOfPerson > 0 && (
                  <div className="bg-white border rounded-md p-3 mt-2 text-sm">
                    <p className="font-medium text-gray-700">
                      👥 This payment covers <span className="font-semibold">{split.noOfPerson}</span> {split.noOfPerson > 1 ? "people" : "person"}.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Based on the total bill, this share represents approximately{" "}
                      <span className="font-semibold">{((split.amount / order.billAmount) * 100).toFixed(1)}%</span> of the total payment.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {order.miniStatement?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Mini Statement</h3>
          <div className="space-y-2">
            {order.miniStatement.map((tx, index) => (
              <div key={index} className="flex justify-between bg-gray-50 p-3 rounded-lg text-sm">
                <div>
                  <p className="font-mono text-gray-700">{tx?.transactionId}</p>
                  <p className="text-xs text-gray-500">Tip: ${tx.tipAmount}</p>
                </div>
                <span className="font-semibold">${(tx.amount + tx.tipAmount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <ConfirmationPopup
        isOpen={confirmOpen}
        setIsOpen={setConfirmOpen}
        getActionValue={handleConfirmAction}
        title="Manual Payment Confirmation"
        message={`Are you sure you want to mark Order #${order.orderId} as paid manually?`}
        confirmButtonLabel="Yes, Confirm"
        cancelButtonLabel="Cancel"
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default OrderDetails;
